import React, { useCallback, useEffect, useState } from 'react';
import * as pdfjs from 'pdfjs-dist';
import Button from '../../../components/ui/Button';
import { ModalShell, ModalHeader } from '../../../components/ui/ModalFrame';
import { MODAL_LAYER } from '../../../constants/modalLayers';
import PdfPageViewer from '../../contract-dashboard/components/signature-field-editor/PdfPageViewer';

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url
).toString();

const noop = () => {};

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {string} [props.documentTitle]
 * @param {() => Promise<{ success: boolean; data?: { blob: Blob }; error?: string }>} props.loadPdf
 */
const UploadedDocumentPdfViewerModal = ({ open, onClose, documentTitle = '', loadPdf }) => {
  const [pdfBlob, setPdfBlob] = useState(null);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [pageDimensions, setPageDimensions] = useState({});

  const resetState = useCallback(() => {
    setPdfBlob(null);
    setLoadingPdf(false);
    setLoadError(null);
    setCurrentPage(1);
    setTotalPages(0);
    setPageDimensions({});
  }, []);

  useEffect(() => {
    if (!open || typeof loadPdf !== 'function') {
      resetState();
      return undefined;
    }

    let cancelled = false;

    const load = async () => {
      setLoadingPdf(true);
      setLoadError(null);
      setPdfBlob(null);

      try {
        const result = await loadPdf();
        if (cancelled) return;

        if (!result?.success) {
          setLoadError(result.error || 'Não foi possível carregar o PDF.');
          return;
        }

        const blob = result.data?.blob;
        if (!blob) {
          setLoadError('PDF indisponível.');
          return;
        }

        setPdfBlob(blob);

        const arrayBuffer = await blob.arrayBuffer();
        const pdf = await pdfjs.getDocument({ data: arrayBuffer }).promise;
        if (cancelled) return;
        setTotalPages(pdf.numPages);
        setCurrentPage(1);
      } catch (err) {
        console.error('UploadedDocumentPdfViewerModal load:', err);
        if (!cancelled) {
          setLoadError('Erro ao carregar o documento.');
        }
      } finally {
        if (!cancelled) {
          setLoadingPdf(false);
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [loadPdf, open, resetState]);

  const handlePageDimensions = useCallback((pageNumber, widthPt, heightPt) => {
    setPageDimensions((prev) => ({
      ...prev,
      [pageNumber]: { widthPt, heightPt },
    }));
  }, []);

  if (!open) return null;

  const title = documentTitle?.trim() || 'Documento';

  return (
    <ModalShell
      zClass={MODAL_LAYER.purchase}
      backdropClassName="bg-black/60"
      backdropPadding="all"
      panelClassName="relative bg-card rounded-xl w-full max-w-[calc(100vw-1.5rem)] xl:max-w-[min(1200px,calc(100vw-2rem))] h-[calc(100dvh-1.5rem)] max-h-[calc(100dvh-1.5rem)] shadow-elevation-lg flex flex-col overflow-hidden"
    >
      <ModalHeader
        title={`Visualizar documento — ${title}`}
        onClose={onClose}
        className="px-4 md:px-5"
      />

      <div className="px-4 md:px-5 py-2 border-b border-border flex-shrink-0 flex justify-end">
        <Button type="button" variant="outline" size="sm" onClick={onClose}>
          Fechar
        </Button>
      </div>

      <div className="flex-1 min-h-0 overflow-hidden">
        <PdfPageViewer
          open={open}
          pdfBlob={pdfBlob}
          fields={[]}
          selectedSignerId={null}
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          onSelectField={noop}
          onUpdateFieldGeometry={noop}
          onAddField={noop}
          loadingPdf={loadingPdf}
          loadError={loadError}
          canAddField={false}
          viewOnly
          pageDimensions={pageDimensions}
          onPageDimensions={handlePageDimensions}
        />
      </div>
    </ModalShell>
  );
};

export default UploadedDocumentPdfViewerModal;
